import {
  type Context,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import jwtDecode from 'jwt-decode';
import { cloneDeep } from 'lodash';
import { useLocation } from 'react-router-dom';

import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import Loading from '~/pages/loading';
import { useGetCalendarAccountQuery } from '~/store/services/user';
import { createPermissionsMap } from '../common/permissions';
import { auth0SilentTokenOptions } from '../constants/global.constants';
import {
  type Auth0AccessToken,
  type Auth0User,
  type MaudPermissions,
  type ProviderRole,
  AdminRoles,
  AUTH0_JWT_USER_EXTERNAL_ID_CLAIM_KEY,
  AUTH0_USER_PERMISSIONS_KEY,
  AUTH0_USER_ROLES_KEY,
  ProviderAccess,
} from '../constants/permissions.constants';
import { useLazyGetProviderByIdQuery } from '~/store/services/providers';
import useHandleLogout from '~/lib/hooks/useHandleLogout';
import useAppMonitor from '~/lib/hooks/useAppMonitor';
import useBootstrapFailureRedirect from '~/lib/hooks/useBootstrapFailureRedirect';

export type TGlobalContext = {
  access: ProviderAccess[];
  /**
   * @deprecated use RTK query
   * @param id
   * @param listener
   */
  addListener: (id: string, listener: (...args: any) => void) => void;
  calendarAccount: CalendarAccount;
  /**
   * @deprecated will be replaced by RTK query in the future. if using for the first time, please access via rtk query
   */
  currentPatientGender: string;
  currentPatientId: string;
  currentUser: GetProviderByIdResponse;
  hasManageUserRole: boolean;
  isCalendarAccountLoading: boolean;
  isOnCalendar: boolean;
  isOnPatientProfile: boolean;
  isOnSettings: boolean;
  /**
   * @deprecated use RTK query
   */
  listenerCallback: (ids: string[], params: any[][]) => void;
  permissions: Record<MaudPermissions, boolean>;
  /**
   * @deprecated use RTK query
   */
  removeListener: (id: string) => void;
  roles: ProviderRole[];
  /**
   * @deprecated will be replaced by RTK query in the future. if using for the first time, please access via rtk query
   */
  setCurrentPatientGender: Dispatch<SetStateAction<string>>;
  setIsOnCalendar: Dispatch<SetStateAction<boolean>>;
  setIsOnPatientProfile: Dispatch<SetStateAction<boolean>>;
  setIsOnSettings: Dispatch<SetStateAction<boolean>>;
};

export const GlobalContext: Context<TGlobalContext> =
  createContext<TGlobalContext>({
    access: [],
    addListener: () => {},
    calendarAccount: null,
    currentPatientGender: '',
    currentPatientId: '',
    currentUser: null,
    hasManageUserRole: false,
    isCalendarAccountLoading: false,
    isOnCalendar: false,
    isOnPatientProfile: false,
    isOnSettings: false,
    listenerCallback: () => {},
    permissions: {} as Record<MaudPermissions, boolean>,
    removeListener: () => {},
    roles: [],
    setCurrentPatientGender: () => {},
    setIsOnCalendar: () => {},
    setIsOnPatientProfile: () => {},
    setIsOnSettings: () => {},
  });

export const GlobalContextProvider = ({ children }: PropsWithChildren) => {
  const { getAccessTokenSilently, user: auth0User } = useAuth0<Auth0User>();

  const { pathname } = useLocation();

  const { identifyProvider } = useAnalytics();
  const appMonitor = useAppMonitor();

  const [listeners, setListeners] = useState<
    Record<string, (...args: any) => void>
  >({});
  const [isOnCalendar, setIsOnCalendar] = useState<boolean>(false);
  const [isOnPatientProfile, setIsOnPatientProfile] = useState<boolean>(false);
  const [isOnSettings, setIsOnSettings] = useState<boolean>(false);
  const [currentPatientGender, setCurrentPatientGender] =
    useState<string>(null);

  const [getProviderById, { data: currentUser, error: currentUserError }] =
    useLazyGetProviderByIdQuery();

  const { data: calendarAccount, isLoading: isCalendarAccountLoading } =
    useGetCalendarAccountQuery(undefined, { skip: !currentUser?.userId });

  const { logout: forceLogout } = useHandleLogout();

  const redirectOnBootstrapFailure = useBootstrapFailureRedirect();
  const bootstrapRedirectFiredRef = useRef(false);

  /**
   * Once the user has authenticated with Auth0, find the user's external ID by
   * generating an Auth0 access token and checking for the "external_user_id" claim.
   * Use this to fetch the user details from the user service.
   */
  useEffect(() => {
    if (auth0User?.sub) {
      (async () => {
        try {
          const accessToken = await getAccessTokenSilently(
            auth0SilentTokenOptions,
          );
          const decoded = jwtDecode<Auth0AccessToken>(accessToken);
          const externalId = decoded[AUTH0_JWT_USER_EXTERNAL_ID_CLAIM_KEY];
          if (externalId) {
            getProviderById({ providerExternalId: externalId }).then(
              (result) => {
                if (result?.data) {
                  const user = result.data;
                  identifyProvider(user);
                  appMonitor.setUser(user);
                }
              },
            );
          } else {
            const authId = auth0User.sub.replace('|', encodeURIComponent('|'));
            throw new Error(
              `External ID not found on access token for user with auth-id ${authId}. Token: ${JSON.stringify(decoded, null, 2)}`,
            );
          }
        } catch (error) {
          const errMsg = `Error retrieving user details: ${error}`;
          console.error(errMsg);
          forceLogout();
        }
      })();
    }
  }, [auth0User?.sub]);

  const permissions = auth0User?.[AUTH0_USER_PERMISSIONS_KEY];

  const access = useMemo<ProviderAccess[]>(() => {
    return (permissions ?? []).filter((permission: string) =>
      (Object.values(ProviderAccess) as string[]).includes(permission),
    ) as ProviderAccess[];
  }, [permissions]);

  const roles = auth0User?.[AUTH0_USER_ROLES_KEY];

  const hasManageUserRole = useMemo<boolean>(() => {
    return (roles ?? []).includes(AdminRoles.ADMIN_MANAGE_USERS);
  }, [roles]);

  /**
    A map of all maud permissions and whether or not the user has that permission, e.g.
      {
        "admin:access:app": false,
        "maud:access:app": true,
        "maud:permission:add-patient": true,
      }
  */
  const permissionsMap = useMemo<Record<MaudPermissions, boolean>>(() => {
    return createPermissionsMap(permissions ?? []);
  }, [permissions]);

  const currentPatientId = useMemo<string>(() => {
    const lastIndex = pathname.endsWith('/')
      ? pathname.length - 1
      : pathname.length;
    const trimmedPathname = pathname.substring(1, lastIndex);
    const parts = trimmedPathname.split('/');
    return parts[0] === 'maud' && parts[1] === 'patients' && !!parts[2]
      ? parts[2]
      : '';
  }, [pathname]);

  const addListener = (id: string, listener: (...args: any) => void) => {
    setListeners((currentState) => ({
      ...currentState,
      [id]: listener,
    }));
  };

  const removeListener = (id: string) => {
    setListeners((currentState) => {
      const newState = cloneDeep(currentState);
      delete newState[id];
      return newState;
    });
  };

  const listenerCallback = (ids: string[], params: any[][]) => {
    ids.forEach((id: string, index: number) => {
      const param = params[index] ?? [];
      listeners[id]?.(...param);
    });
  };

  useEffect(() => {
    if (currentUserError && !bootstrapRedirectFiredRef.current) {
      bootstrapRedirectFiredRef.current = true;
      redirectOnBootstrapFailure(currentUserError, 'getProviderById');
    }
  }, [currentUserError, redirectOnBootstrapFailure]);

  if (currentUserError) {
    return <Loading testId="global-current-user-redirect" />;
  }
  if (!currentUser?.userId) {
    return <Loading testId="global-current-user-loading" />;
  }

  return (
    <GlobalContext.Provider
      value={{
        access,
        addListener,
        calendarAccount,
        currentPatientGender,
        currentPatientId,
        currentUser,
        hasManageUserRole,
        isCalendarAccountLoading,
        isOnCalendar,
        isOnPatientProfile,
        isOnSettings,
        listenerCallback,
        permissions: permissionsMap,
        removeListener,
        roles: (roles ?? []) as ProviderRole[],
        setCurrentPatientGender,
        setIsOnCalendar,
        setIsOnPatientProfile,
        setIsOnSettings,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = (): TGlobalContext => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error(
      'useGlobalContext must be used within a GlobalContextProvider.',
    );
  }
  return context;
};
